import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero, SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { CTABanner } from "@/components/CTABanner";
import { PROCESS } from "@/data/content";

const Chapter = ({ chapter, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const flip = index % 2 === 1;

  return (
    <div ref={ref} className="grid items-center gap-10 lg:grid-cols-2" data-testid={`process-chapter-${chapter.chapter}`}>
      <Reveal className={flip ? "lg:order-2" : ""}>
        <div className="relative">
          <span className="pointer-events-none absolute -top-14 left-0 font-serif text-[7rem] font-semibold leading-none text-sand sm:text-[9rem]" aria-hidden="true">
            {chapter.chapter}
          </span>
          <div className="relative pt-16 sm:pt-20">
            <p className="eyebrow">{chapter.subtitle}</p>
            <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight text-charcoal sm:text-4xl">{chapter.title}</h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-charcoal-soft sm:text-base">{chapter.desc}</p>
            <ul className="mt-6 space-y-3">
              {chapter.points.map((pt) => (
                <li key={pt} className="flex items-start gap-3 text-sm font-medium text-charcoal">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  {pt}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.12} className={flip ? "lg:order-1" : ""}>
        <motion.div style={{ y: yImg }} className="gold-ring overflow-hidden rounded-[2rem] shadow-luxury">
          <img src={chapter.image} alt={chapter.title} className="h-80 w-full object-cover sm:h-[26rem]" loading="lazy" />
        </motion.div>
      </Reveal>
    </div>
  );
};

const TIMELINE = [
  "Key collection",
  "Book via WhatsApp",
  "Visit 1 · Defect check",
  "Report in 24h",
  "Visit 2 · BSC joint inspection",
  "Developer rectifies",
  "Visit 3 · Verification",
  "Renovation-ready",
];

export default function Process() {
  return (
    <>
      <SEO
        title="Our 3-Visit Inspection Process | Defect Check to BSC Sign-Off — Lobang"
        description="Visit 1: comprehensive defect check. Visit 2: joint inspection with the developer's BSC. Visit 3: post-rectification verification. Singapore BTO, condo and landed homes."
      />
      <PageHero
        eyebrow="The Process"
        title="Three visits. One flawless home."
        copy="A deliberate journey from key collection to renovation-ready — with you represented at every step."
      />

      <section className="space-y-24 px-5 py-16 sm:px-8 sm:py-24 lg:space-y-32" data-testid="process-chapters">
        <div className="mx-auto max-w-7xl space-y-24 lg:space-y-32">
          {PROCESS.map((p, i) => (
            <Chapter key={p.chapter} chapter={p} index={i} />
          ))}
        </div>
      </section>

      <section className="bg-cream px-5 py-20 sm:px-8 sm:py-24" data-testid="process-timeline">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            eyebrow="A Typical Journey"
            title="Eight steps from keys to confidence"
          />
          <div className="mt-12 flex gap-4 overflow-x-auto pb-4 lg:grid lg:grid-cols-4 lg:overflow-visible">
            {TIMELINE.map((step, i) => (
              <Reveal key={step} delay={i * 0.05} className="min-w-[11rem]">
                <div className="flex h-full flex-col rounded-2xl border border-border bg-white p-5 shadow-luxury">
                  <span className="font-serif text-sm italic text-gold-dark">Step {i + 1}</span>
                  <span className="mt-2 text-sm font-semibold text-charcoal">{step}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10 text-center">
            <Link
              to="/pricing"
              data-testid="process-pricing-link"
              className="inline-flex items-center gap-2 rounded-full bg-charcoal px-7 py-3.5 text-sm font-semibold text-ivory transition-colors duration-300 hover:bg-gold-dark"
            >
              Compare One-Trip & Three-Trip pricing
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CTABanner
        title="Your keys are coming. Are you ready?"
        copy="Book Visit 1 before your defect liability period starts ticking. WhatsApp us your key collection date now."
      />
    </>
  );
}
